import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveCompany } from '~/hooks/useActiveCompany';
import useDataTestId from '~/hooks/useDataTestId';
import colors from '~/theme/colors.module.scss';
import { Counterpart } from '~/types/counterparts';
import { isDefined } from '~/types/typeGuards';
import { isTheCurrentActiveCompany } from '~/utils/counterparts';
import IdentityTypeThumb from './IdentityTypeThumb';
import { HStack, VStack } from '../layout/Stack';
import Text from '../typographies/Text';

interface CounterpartSummaryRowProps {
  counterpart: Counterpart;
  className?: string;
  style?: CSSProperties;
  dataTestId?: string;
  thumbBackgroundColor?: string;
}

const capitalize = (val: string) => startCase(toLower(val));

export const useCounterpartDetails = (counterpart: Counterpart) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'summary_row' });

  const baseDetails = [
    capitalize(counterpart.address ?? ''),
    `${capitalize(counterpart.city ?? '')}${counterpart.provinceCode ? ` (${counterpart.provinceCode})` : ''}`,
    counterpart.taxNumber ? t('tax_number_label', { value: counterpart.taxNumber }) : '',
    counterpart.vatNumber ? t('vat_number_label', { value: counterpart.vatNumber }) : '',
    isDefined(counterpart.destinationCode) && counterpart.destinationCode !== '0000000'
      ? t('recipient_code_label', { value: counterpart.destinationCode })
      : '',
    isDefined(counterpart.bankIdentifier) && counterpart.bankIdentifier,
  ];

  return baseDetails.filter((x) => x).join(' • ');
};

export const CounterpartName: React.FC<{
  dataTestId?: string;
  counterpart: Counterpart;
  isCurrentActiveCompany?: boolean;
}> = ({ dataTestId, counterpart, isCurrentActiveCompany: isCurrentActiveCompany }) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'summary_row' });
  return (
    <Text variant="body2" weight="semibold" dataTestId={dataTestId}>
      {counterpart.companyName}
      {isCurrentActiveCompany && ` ${t('active_company')}`}
    </Text>
  );
};

const CounterpartSummaryRow: React.FC<CounterpartSummaryRowProps> = ({
  counterpart,
  className,
  style,
  thumbBackgroundColor,
  dataTestId,
}) => {
  const dt = useDataTestId(dataTestId);
  const details = useCounterpartDetails(counterpart);
  const isCurrentActiveCompany = isTheCurrentActiveCompany(counterpart, useActiveCompany());
  const isCurrentActiveCompanyBGColor = isCurrentActiveCompany ? colors.colorViolet300 : undefined;

  return (
    <HStack align="center" gap={2} className={className} style={style}>
      <IdentityTypeThumb
        value={counterpart.identityType}
        backgroundColor={thumbBackgroundColor || isCurrentActiveCompanyBGColor}
      />
      <VStack gap={0.5}>
        <CounterpartName
          counterpart={counterpart}
          dataTestId={dt('companyName')}
          isCurrentActiveCompany={isCurrentActiveCompany}
        />
        <Text variant="body2" weight="regular" secondaryColor dataTestId={dt('details')}>
          {details}
        </Text>
      </VStack>
    </HStack>
  );
};

export default CounterpartSummaryRow;
